/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { sizeToCSS } from '../utils/UNSAFE_size';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useEffect, useRef } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '../hooks/PRIVATE_useDvtMeterEvents';

import {
  getMeterAriaProps,
  validateRange,
  getTrackAndIndicatorColor
} from '../utils/PRIVATE_meterUtils';

import { Threshold } from '../utils/UNSAFE_meterTypes';
import { BarTrack, BarValue } from '../PRIVATE_Meter';
import { getDimensions, getRefLinesInfo, getValue } from './MeterUtils';
import { ReferenceLineBar } from './ReferenceLines';
import { ThresholdBar } from './ThresholdBar';
import { MeterBarProps } from './MeterBar.types';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { MeterBarStyles, MeterBarVariantOptions } from './themes/MeterBarStyles.css';
import { MeterBarRedwoodTheme } from './themes/redwood/MeterBarTheme';

/**
 * A Meter bar displays information graphically in a rectangular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function MeterBar({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  length = '100%',
  size,
  orientation = 'horizontal',
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  ...props
}: MeterBarProps) {
  validateRange(min, max, value, step);
  const currentInputRef = useRef<number>();

  const isHoriz = orientation === 'horizontal';
  const barTrackRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef(getDimensions(null));
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  useEffect(() => {
    if (props.onCommit || props.onInput) {
      dimensionsRef.current = getDimensions(barTrackRef.current);
    }
  }, [size, length, orientation, props.onCommit, props.onInput]);

  const getValueFromEvent = (event: PointerEvent): number | undefined => {
    // only evaluate value from bar track
    if (event.target == barTrackRef.current) {
      return getValue(event, dimensionsRef, min, max, step, orientation, isRtl);
    }
    return;
  };

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    barTrackRef,
    currentInputRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    currentInputRef,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    props.datatip,
    props['aria-describedby']
  );

  const ariaLabelProps = getMeterAriaProps(
    value,
    min,
    max,
    props['aria-label'],
    props['aria-labelledby'],
    props.thresholds
  );
  const mergedProps = mergeProps(
    pointerEventsProps,
    keyboardEventsProps,
    datatipProps,
    ariaLabelProps
  );

  const validIndicatorSize = Math.max(0, Math.min(1, indicatorSize));
  const valueBarLength = `${((value - min) * 100) / (max - min)}%`;
  const valueBarSize = `${validIndicatorSize * 100}%`;
  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const sizeStyle = getRefLinesInfo(props.referenceLines);
  const isInteractive = props.onCommit || props.onInput;
  const { isTabbable } = useTabbableMode();

  const {
    classes,
    styles: {
      interactiveStyle,

      barValueBaseStyle,
      barValueVerticalStyle,
      barValueHorizontalStyle,

      barTrackBaseStyle,

      barTrackHorizontalSizeStyle,
      barTrackVerticalSizeStyle,

      barTrackMarginTopStyle,
      barTrackMarginStartStyle,
      barTrackMarginBottomStyle,
      barTrackMarginEndStyle,

      trackSectionHorizontalFirstStyle,
      trackSectionHorizontalLastStyle,

      trackSectionVerticalFirstStyle,
      trackSectionVerticalLastStyle,

      trackSectionRoundedCornersStyle
    }
  } = useComponentTheme<MeterBarVariantOptions, MeterBarStyles>(MeterBarRedwoodTheme, {
    size,
    orientation
  });

  return (
    <>
      <div
        tabIndex={!isTabbable ? -1 : 0}
        {...mergedProps}
        style={{
          width: isHoriz ? sizeToCSS(length) : undefined,
          height: isHoriz ? undefined : sizeToCSS(length)
        }}
        class={classNames([classes])}>
        <BarTrack
          ref={barTrackRef}
          orientation={orientation}
          color={
            !isTrackRendered || (thresholdDisplay === 'all' && props.thresholds)
              ? 'transparent'
              : trackColor
          }
          class={classNames([
            barTrackBaseStyle,
            isInteractive ? interactiveStyle : '',
            isHoriz ? barTrackHorizontalSizeStyle : barTrackVerticalSizeStyle,
            ...getMarginClass(
              sizeStyle,
              isHoriz,
              barTrackMarginTopStyle,
              barTrackMarginStartStyle,
              barTrackMarginBottomStyle,
              barTrackMarginEndStyle
            )
          ])}>
          {thresholdDisplay === 'all' &&
            props.thresholds
              ?.filter((threshold: Threshold) => threshold.max > min && threshold.max <= max)
              .sort((a, b) => a.max - b.max)
              .map((threshold: Threshold, index, thresholds) => {
                const diff =
                  index === 0 ? threshold.max - min : threshold.max - thresholds[index - 1].max;
                const trackSectionLastStyle = isHoriz
                  ? trackSectionHorizontalLastStyle
                  : trackSectionVerticalLastStyle;
                let trackSectionStyleClass =
                  index === 0
                    ? isHoriz
                      ? trackSectionHorizontalFirstStyle
                      : trackSectionVerticalFirstStyle
                    : '';
                if (threshold.max === max)
                  trackSectionStyleClass =
                    index === 0
                      ? trackSectionRoundedCornersStyle // Single threshold from min to max
                      : trackSectionLastStyle;
                return (
                  <>
                    <ThresholdBar
                      class={trackSectionStyleClass}
                      orientation={orientation}
                      length={`${(diff * 100) / (max - min)}%`}
                      color={threshold.color}
                    />
                    {isTrackRendered && index === thresholds.length - 1 && threshold.max < max ? (
                      <ThresholdBar
                        class={trackSectionLastStyle}
                        color={trackColor}
                        orientation={orientation}
                        length={`${((max - thresholds[index].max) * 100) / (max - min)}%`}
                      />
                    ) : (
                      ''
                    )}
                  </>
                );
              })}
          <BarValue
            length={valueBarLength}
            size={valueBarSize}
            orientation={orientation}
            class={classNames([
              barValueBaseStyle,
              isHoriz ? barValueHorizontalStyle : barValueVerticalStyle
            ])}
            color={indicatorColor}
          />
          {props.referenceLines?.map((item) => (
            <ReferenceLineBar
              color={item.color}
              orientation={orientation}
              percentage={`${((item.value - min) * 100) / (max - min)}%`}
              position={item.position}
            />
          ))}
        </BarTrack>
      </div>
      {datatipContent}
    </>
  );
}

const getMarginClass = function (
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  barTrackMarginTopStyle: string,
  barTrackMarginStartStyle: string,
  barTrackMarginBottomStyle: string,
  barTrackMarginEndStyle: string
) {
  const className = [];

  if (info.start) {
    className.push(isHoriz ? barTrackMarginTopStyle : barTrackMarginStartStyle);
  }

  if (info.end) {
    className.push(isHoriz ? barTrackMarginBottomStyle : barTrackMarginEndStyle);
  }
  return className;
};
